import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { toast } from 'utils/notify'
import { FirestoreDatabase } from '../index'
import { Collection, Roles } from 'constants/index'
import { signOut } from 'store/auth'
import { cloudFunctionsInstance } from 'config/axios'
import { getConferenceRole, isConferenceUser } from 'utils/conference'

export const fetchAllUsers = createAsyncThunk(
  'users/fetchAllUsers',
  async ({ conferenceId, currentUserRole = Roles.SUPER_ADMIN }, { rejectWithValue }) => {
    if (!conferenceId) return []
    try {
      // Filter on the conference assignment only — never on `role`. A Firestore filter
      // on a field, `!=` just as much as `==`, matches only documents where that field
      // exists, and the backend writes no doc-level `role` when it assigns an
      // already-existing account to a conference. Filtering on it here therefore hid
      // every such conference user from the list entirely.
      const [assignedSnapshot, legacySnapshot] = await Promise.all([
        FirestoreDatabase.collection(Collection.USERS)
          .where('conferenceIds', 'array-contains', conferenceId)
          .get(),
        // Docs predating the conferenceIds array carry only the scalar field, and
        // array-contains cannot see them.
        FirestoreDatabase.collection(Collection.USERS)
          .where('conferenceId', '==', conferenceId)
          .get()
      ])

      const usersByUid = new Map()
      assignedSnapshot.docs.concat(legacySnapshot.docs).forEach((doc) => {
        usersByUid.set(doc.id, { uid: doc.id, ...doc.data() })
      })

      const canManageBoth =
        currentUserRole === Roles.SUPER_ADMIN || currentUserRole === Roles.ORGANIZER

      return Array.from(usersByUid.values()).filter((user) => {
        if (!isConferenceUser(user)) return false
        // An owner manages this conference's admins only, never its other owners. The
        // check reads the role for *this* conference, not the account-level one.
        return canManageBoth || getConferenceRole(user, conferenceId) === Roles.ADMIN
      })
    } catch (error) {
      console.error(error)
      return rejectWithValue(error)
    }
  }
)

// Returns { alreadyAssigned: true, conferences: [{id, confName}] } when the email is already
// assigned to other active conferences and force is not set. Caller must show confirmation dialog
// and re-call with force: true to proceed.
export const createUser = async (payload) => {
  const response = await cloudFunctionsInstance.post('/createConferenceAdmin', payload)
  if (response.data.alreadyAssigned) {
    return { alreadyAssigned: true, conferences: response.data.conferences }
  }

  toast.success('Admin account created.')
  if (response.data.isEmailSent) {
    toast.success('Activation email sent to the admin.')
  } else {
    toast.error('Activation email could not be sent.')
  }
  return { alreadyAssigned: false }
}

export const fetchUserByUserId = createAsyncThunk(
  'users/fetchUserByUserId',
  async (uid, { rejectWithValue }) => {
    try {
      const userDoc = await FirestoreDatabase.collection(Collection.USERS).doc(uid).get()
      return { uid: userDoc.id, ...userDoc.data() }
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)

export const deleteUserByUserId = createAsyncThunk(
  'users/deleteUserByUserId',
  async ({ uid, confId }, { rejectWithValue }) => {
    try {
      return await cloudFunctionsInstance.delete(
        `/deleteConferenceAdmin?uid=${uid}&confId=${confId}`
      )
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)

export const updateUserPasswordByUserId = createAsyncThunk(
  'users/updateUserPasswordByUserId',
  async (requestData, { rejectWithValue }) => {
    try {
      return await cloudFunctionsInstance.post('/updateConferenceAdmin', {
        ...requestData
      })
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)

export const resendActivationEmail = createAsyncThunk(
  'users/resendActivationEmail',
  async ({ uid, callerUid }, { rejectWithValue }) => {
    try {
      return await cloudFunctionsInstance.post('/resendActivationEmail', { uid, callerUid })
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)

export const initialState = {
  error: null,
  inProgress: false,
  admins: [],
  admin: {},
  confFilter: '',
  isCreatedOrUpdated: false,
  currentPage: 0,
  pageSize: 10
}

const adminsSlice = createSlice({
  name: 'attendee',
  initialState: initialState,
  reducers: {
    setError: (state, action) => {
      state.error = action.payload
    },
    setProgress: (state, action) => {
      state.inProgress = action.payload
    },
    setAdmins: (state, action) => {
      state.admins = action.payload
    },
    setConfFilter: (state, action) => {
      state.confFilter = action.payload
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload
    },
    setIsCreatedOrUpdated: (state, action) => {
      state.isCreatedOrUpdated = action.payload
    }
  },
  extraReducers: {
    [fetchAllUsers.pending]: (state) => {
      state.inProgress = true
    },
    [fetchAllUsers.fulfilled]: (state, action) => {
      state.inProgress = false
      state.admins = action.payload
      state.error = null
    },
    [fetchAllUsers.rejected]: (state, action) => {
      state.inProgress = false
      state.error = action.payload
    },
    [fetchUserByUserId.pending]: (state) => {
      state.inProgress = true
    },
    [fetchUserByUserId.fulfilled]: (state, action) => {
      state.inProgress = false
      state.admin = action.payload
      state.error = null
    },
    [fetchUserByUserId.rejected]: (state, action) => {
      state.inProgress = false
      state.error = action.payload
    },

    [deleteUserByUserId.pending]: (state) => {
      state.inProgress = true
    },
    [deleteUserByUserId.fulfilled]: (state, action) => {
      state.inProgress = false
      const { uid, msg } = action.payload.data
      state.admins = state.admins.filter((row) => row.uid !== uid)
      state.error = null
      toast.success(msg)
    },
    [deleteUserByUserId.rejected]: (state, action) => {
      state.inProgress = false
      state.error = action.payload
    },

    [updateUserPasswordByUserId.fulfilled]: (state, action) => {
      const { msg } = action.payload.data
      state.error = null
      toast.success(msg)
    },
    [updateUserPasswordByUserId.rejected]: (state, action) => {
      state.error = action.payload
    },

    [resendActivationEmail.fulfilled]: (state, action) => {
      state.error = null
      const { isEmailSent } = action.payload.data
      if (isEmailSent) {
        toast.success('Activation email resent.')
      } else {
        toast.error('Activation email could not be sent.')
      }
    },
    [resendActivationEmail.rejected]: (state, action) => {
      state.error = action.payload
      toast.error(action.payload?.response?.data?.msg || 'Could not resend the activation email.')
    },
    [signOut.fulfilled]: (state) => {
      state.error = null
      state.inProgress = false
      state.admins = []
      state.admin = {}
      state.confFilter = ''
      state.isCreatedOrUpdated = false
      state.currentPage = 0
      state.pageSize = 10
    }
  }
})

export default adminsSlice
